import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  FileDown,
  Gift,
  ShoppingBag,
  DollarSign,
  FileText,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_complimentary_reports,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";
import { formatDateString } from "../CommonJquery/CommonJquery";
import { retrieveData } from "../LocalConnection/LocalConnection";

export default function ComplimentaryReport() {
  const { toast } = useToast();
  const restroName = retrieveData("restaurant_name");

  // Date states
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(new Date());

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [complimentaryData, setComplimentaryData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalComplimentaryAmount: 0,
    totalTaxableAmount: 0,
    totalGrandTotal: 0,
  });

  // Fetch data
  const fetchComplimentaryData = useCallback(
    async (start_date, end_date, flag = "1", call_id = "0") => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", start_date.toISOString().split("T")[0]);
      fd.append("end_date", end_date.toISOString().split("T")[0]);
      fd.append("flag", flag);
      fd.append("call_id", call_id);

      try {
        const response = await server_post_data(get_complimentary_reports, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const message = response.data.message || {};
          const compArray = Array.isArray(message.complimentary_data)
            ? message.complimentary_data
            : [];

          setComplimentaryData(compArray);

          if (compArray.length !== 0) {
            const totalComplimentaryAmount = compArray.reduce(
              (sum, item) => sum + parseFloat(item.discount_amount || 0),
              0
            );
            const totalTaxableAmount = compArray.reduce(
              (sum, item) => sum + parseFloat(item.product_amount || 0),
              0
            );
            const totalGrandTotal = compArray.reduce(
              (sum, item) => sum + parseFloat(item.final_amount || 0),
              0
            );

            setStats({
              totalOrders: compArray.length,
              totalComplimentaryAmount,
              totalTaxableAmount,
              totalGrandTotal,
            });
          } else {
            setStats({
              totalOrders: 0,
              totalComplimentaryAmount: 0,
              totalTaxableAmount: 0,
              totalGrandTotal: 0,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching complimentary data:", error);
        toast({
          title: "Error",
          description: "Failed to load complimentary data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast]
  );

  // Export to Excel — matches the uploaded report format exactly
  const exportToExcel = useCallback(() => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

    // ── Header block (rows 1-4) ──────────────────────────────────────────────
    const headerMeta = [
      ["Name:", "Restaurant Wise Complimentary Orders Summary"],
      ["Restaurant Name:", restroName || "-"],
      ["Restaurant Address:", retrieveData("restaurant_address") || "-"],
      [],
      // Column headers (row 5)
      [
        "Created Date",
        "Customer Name",
        "Customer Phone",
        "Reason",
        "GSTIN",
        "Order No.",
        "Order Type",
        "Taxable Amount (₹)",
        "Discount (₹)",
        "Container Charge (₹)",
        "Tax (₹)",
        "Round Off (₹)",
        "Grand Total (₹)",
      ],
    ];

    // ── Data rows ────────────────────────────────────────────────────────────
    const dataRows = complimentaryData.map((row) => {
      const orderType = parseInt(row.order_type);
      const orderTypeLabel =
        orderType === 0 || orderType === 3
          ? "Dine In"
          : orderType === 2
            ? "Delivery"
            : "Takeaway";
      const tableName = row.table_code_names
        ? ` (${row.table_code_names})`
        : "";

      return [
        formatDateString(row.entry_date) || "-",
        row.guest_name || "-",
        row.guest_mobile_no || "-",
        row.discount_reason || "-",
        row.gstin || "-",
        row.invoice_id || "-",
        orderTypeLabel + tableName,
        parseFloat(row.product_amount || 0).toFixed(2),
        parseFloat(row.discount_amount || 0).toFixed(2),
        parseFloat(row.container_charge || 0).toFixed(2),
        parseFloat(row.tax_amount || 0).toFixed(2),
        parseFloat(row.roundoff_amount || 0).toFixed(2),
        parseFloat(row.final_amount || 0).toFixed(2),
      ];
    });

    const allRows = [...headerMeta, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(allRows);

    // Column widths
    ws["!cols"] = [
      { wch: 14 }, // Created Date
      { wch: 18 }, // Customer Name
      { wch: 15 }, // Customer Phone
      { wch: 20 }, // Reason
      { wch: 16 }, // GSTIN
      { wch: 10 }, // Order No.
      { wch: 16 }, // Order Type
      { wch: 20 }, // Taxable Amount
      { wch: 14 }, // Discount
      { wch: 20 }, // Container Charge
      { wch: 10 }, // Tax
      { wch: 12 }, // Round Off
      { wch: 15 }, // Grand Total
    ];

    // Style column header row (row index 4, i.e. the 5th row)
    const colHeaderRowIndex = 4;
    const colCount = 13;
    for (let C = 0; C < colCount; C++) {
      const cellAddr = XLSX.utils.encode_cell({ r: colHeaderRowIndex, c: C });
      if (!ws[cellAddr]) continue;
      ws[cellAddr].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F46E5" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    // Style meta header rows (rows 0-2)
    for (let R = 0; R < 3; R++) {
      const cellAddr = XLSX.utils.encode_cell({ r: R, c: 0 });
      if (ws[cellAddr]) {
        ws[cellAddr].s = { font: { bold: true } };
      }
    }

    const wb = {
      Sheets: { "Complimentary Report": ws },
      SheetNames: ["Complimentary Report"],
    };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(
      data,
      `complimentary_report_${new Date().toISOString().split("T")[0]}.xlsx`
    );

    toast({
      title: "Success",
      description: "Report exported successfully",
    });
  }, [complimentaryData, toast, restroName]);

  // Pagination
  const totalPages = Math.ceil(complimentaryData.length / entriesPerPage);
  const currentData = complimentaryData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Initial load
  useEffect(() => {
    fetchComplimentaryData(startDate, endDate, "1", "0");
  }, []);

  return (
    <MainLayout>
      <Header
        title="Complimentary Report"
        subtitle="View and analyze complimentary orders and amounts"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalOrders}
                </p>
                <p className="text-sm text-muted-foreground">Total Orders</p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalTaxableAmount.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Taxable Amount</p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Gift className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalComplimentaryAmount.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Complimentary Amount
                </p>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalGrandTotal.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Grand Total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={exportToExcel}
            className="gap-2"
            disabled={complimentaryData.length === 0}
          >
            <FileDown className="w-4 h-4" />
            Export Excel
          </Button>
        </div>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Sr.No</TableHead>
                <TableHead>Created Date</TableHead>
                <TableHead>Customer Name</TableHead>
                <TableHead>Customer Phone</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>Order No.</TableHead>
                <TableHead>Order Type</TableHead>
                <TableHead className="text-right">Taxable Amount (₹)</TableHead>
                <TableHead className="text-right">Discount (₹)</TableHead>
                <TableHead className="text-right">
                  Container Charge (₹)
                </TableHead>
                <TableHead className="text-right">Tax (₹)</TableHead>
                <TableHead className="text-right">Round Off (₹)</TableHead>
                <TableHead className="text-right">Grand Total (₹)</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {showLoaderAdmin ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                currentData.map((row, index) => (
                  <TableRow key={index} className="border-border">
                    {/* Sr No */}
                    <TableCell>
                      {(currentPage - 1) * entriesPerPage + index + 1}
                    </TableCell>

                    {/* Created Date */}
                    <TableCell>{formatDateString(row.entry_date)}</TableCell>

                    {/* Customer Name */}
                    <TableCell>{row.guest_name || "-"}</TableCell>

                    {/* Customer Phone */}
                    <TableCell>{row.guest_mobile_no || "-"}</TableCell>

                    {/* Reason */}
                    <TableCell>{row.discount_reason || "-"}</TableCell>

                    {/* GSTIN */}
                    <TableCell>{row.gstin || "-"}</TableCell>

                    {/* Order No */}
                    <TableCell>{row.invoice_id || "-"}</TableCell>

                    {/* Order Type */}
                    <TableCell>
                      {parseInt(row.order_type) === 0 ||
                        parseInt(row.order_type) === 3
                        ? "Dine In"
                        : parseInt(row.order_type) === 2
                          ? "Delivery"
                          : "Takeaway"}
                      {row.table_code_names
                        ? ` (${row.table_code_names})`
                        : ""}
                    </TableCell>

                    {/* Taxable Amount */}
                    <TableCell className="text-right">
                      ₹{parseFloat(row.product_amount || 0).toFixed(2)}
                    </TableCell>

                    {/* Discount / Complimentary Amount */}
                    <TableCell className="text-right text-destructive font-semibold">
                      ₹{parseFloat(row.discount_amount || 0).toFixed(2)}
                    </TableCell>

                    {/* Container Charge */}
                    <TableCell className="text-right">
                      ₹{parseFloat(row.container_charge || 0).toFixed(2)}
                    </TableCell>

                    {/* Tax */}
                    <TableCell className="text-right">
                      ₹{parseFloat(row.tax_amount || 0).toFixed(2)}
                    </TableCell>

                    {/* Round Off */}
                    <TableCell className="text-right">
                      ₹{parseFloat(row.roundoff_amount || 0).toFixed(2)}
                    </TableCell>

                    {/* Grand Total */}
                    <TableCell className="text-right font-bold">
                      ₹{parseFloat(row.final_amount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(
                  currentPage * entriesPerPage,
                  complimentaryData.length
                )}{" "}
                of {complimentaryData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}